import axios from 'axios';
import { getToken } from '../utils/auth';
import { API_BASE_URL } from '../config/runtime';

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
});

// 요청 인터셉터 - 토큰 자동 추가
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 프로젝트 타입 정의
export interface ProjectData {
  category: string;
  subcategory?: string;
  workType: 'outsourcing' | 'remote' | 'onsite' | 'hybrid';
  title: string;
  shortDescription?: string;
  budgetMin: number;
  budgetMax: number;
  budgetType: 'project' | 'hourly' | 'negotiable' | 'fixed';
  startDate?: string;
  durationDays: number;
  description?: string;
  requiredSkills?: string[];
  attachments?: string[];
  experienceLevel?: 'junior' | 'intermediate' | 'senior';
}

export interface Project extends ProjectData {
  id: number;
  clientId: number;
  clientNickname: string;
  status: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

// Project API
export const projectAPI = {
  // 프로젝트 생성
  createProject: async (projectData: ProjectData) => {
    console.log('API: 프로젝트 생성 요청', projectData);
    
    // 카테고리 문자열을 category_id로 변환
    const categoryMap: { [key: string]: number } = {
      'design': 1,
      'development': 2,
      'marketing': 3,
      'writing': 4,
      'video': 5,
      'music': 6,
      'business': 7,
      'other': 8
    };

    const category_id = categoryMap[projectData.category];
    if (!category_id) {
      throw new Error(`유효하지 않은 카테고리: ${projectData.category}`);
    }

    // 백엔드 API 포맷에 맞게 데이터 변환
    const backendData = {
      category_id,
      title: projectData.title,
      description: projectData.description || '',
      budget_min: projectData.budgetMin,
      budget_max: projectData.budgetMax,
      budget_type: projectData.budgetType,
      deadline: projectData.startDate,
      duration_days: projectData.durationDays,
      work_type: projectData.workType,
      location: undefined,
      required_skills: projectData.requiredSkills,
      experience_level: projectData.experienceLevel,
      attachments: projectData.attachments,
      is_urgent: false
    };

    console.log('API: 변환된 프로젝트 데이터', backendData);
    const response = await api.post('/projects', backendData);
    console.log('API: 프로젝트 생성 응답', response.data);
    return response.data;
  },

  // 프로젝트 목록 조회
  getProjects: async (params?: {
    category?: string;
    search?: string;
    minBudget?: number;
    maxBudget?: number;
    workType?: string;
    experienceLevel?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }) => {
    console.log('API: 프로젝트 목록 조회', params);
    const response = await api.get('/projects', { params });
    console.log('API: 프로젝트 목록 응답', response.data);
    return response.data;
  },

  // 프로젝트 상세 조회
  getProjectById: async (id: number) => {
    console.log('API: 프로젝트 상세 조회', id);
    const response = await api.get(`/projects/${id}`);
    console.log('API: 프로젝트 상세 응답', response.data);
    return response.data;
  },

  // 클라이언트별 프로젝트 조회
  getProjectsByClient: async (nickname: string) => {
    console.log('API: 클라이언트별 프로젝트 조회', nickname);
    const response = await api.get(`/projects/client/${nickname}`);
    console.log('API: 클라이언트별 프로젝트 응답', response.data);
    return response.data;
  },

  // 사용자 ID로 프로젝트 조회 (내 프로젝트 목록)
  // 보안: nickname을 URL에 전송하지만 백엔드에서는 JWT의 user ID를 사용
  getProjectsByUserId: async (userId: number, nickname: string) => {
    console.log('API: 사용자 ID로 프로젝트 조회', { userId, nickname });
    // nickname을 URL에 포함하지만, 백엔드는 JWT 토큰의 user ID로 조회
    const response = await api.get(`/projects/client/${nickname}`);
    console.log('API: 사용자 프로젝트 응답', response.data);
    return response.data;
  },

  // 프로젝트 수정
  updateProject: async (id: number, projectData: Partial<ProjectData>) => {
    console.log('API: 프로젝트 수정 요청', { id, projectData });
    const response = await api.put(`/projects/${id}`, projectData);
    console.log('API: 프로젝트 수정 응답', response.data);
    return response.data;
  },

  // 프로젝트 상태 변경
  updateProjectStatus: async (id: number, status: string) => {
    console.log('API: 프로젝트 상태 변경', { id, status });
    const response = await api.patch(`/projects/${id}/status`, { status });
    console.log('API: 프로젝트 상태 변경 응답', response.data);
    return response.data;
  },

  // 프로젝트 삭제
  deleteProject: async (id: number) => {
    console.log('API: 프로젝트 삭제 요청', id);
    const response = await api.delete(`/projects/${id}`);
    console.log('API: 프로젝트 삭제 응답', response.data);
    return response.data;
  },

  // 프로젝트 카테고리 목록
  getCategories: () => {
    return [
      { 
        value: 'design', 
        label: '디자인',
        subcategories: ['로고·브랜딩', '상세·이벤트페이지', 'PPT·인포그래픽', '캐릭터·이모티콘']
      },
      { 
        value: 'development', 
        label: '개발·프로그래밍',
        subcategories: ['웹사이트', '모바일앱', '프로그램', '블록체인']
      },
      { 
        value: 'marketing', 
        label: '마케팅',
        subcategories: ['SNS 마케팅', '광고 운영', 'SEO', '바이럴 마케팅']
      },
      { 
        value: 'writing', 
        label: '글쓰기·번역',
        subcategories: ['블로그·콘텐츠', '번역', '네이밍', '원고·자료 작성']
      },
      { 
        value: 'video', 
        label: '영상·사진',
        subcategories: ['영상 편집', '영상 촬영', '사진 촬영', '사진 편집']
      },
      { 
        value: 'music', 
        label: '음악·오디오',
        subcategories: ['작곡·편곡', '보컬·가이드', '믹싱·마스터링', '더빙·내레이션']
      },
      { 
        value: 'business', 
        label: '비즈니스 컨설팅',
        subcategories: ['법률 자문', '세무·회계', '특허·상표', 'HR·노무']
      },
      { 
        value: 'other', 
        label: '기타',
        subcategories: ['기타']
      },
    ];
  },
};

export default api;
